'use client';

import Image from 'next/image';
import { Check, X } from 'lucide-react';
import { useRef } from 'react';
import { useLoginDialog } from '@/components/login-dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/auth-context';
import { useLanguage } from '@/contexts/language-context';
import { normalizeUserLevel } from '@/lib/feature-permissions';
import { getPricingUrl, getSignUpUrl } from '@/lib/pricing-url';
import { cn } from '@/lib/utils';

type UserLevel = ReturnType<typeof normalizeUserLevel>;
type Translate = ReturnType<typeof useLanguage>['t'];

interface EntitlementAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  level?: unknown;
}

interface Benefit {
  title: string;
  description: string;
}

interface TierCopy {
  title: string;
  subtitle: string;
  benefits: Benefit[];
  primary: string;
}

function getTierCopy(level: UserLevel, t: Translate): TierCopy {
  if (level === 'L0') {
    return {
      title: t('entitlementAccess.l0Title'),
      subtitle: t('entitlementAccess.l0Subtitle'),
      benefits: [
        {
          title: t('entitlementAccess.l0BenefitSessionsTitle'),
          description: t('entitlementAccess.l0BenefitSessionsDescription'),
        },
        {
          title: t('entitlementAccess.l0BenefitChartTitle'),
          description: t('entitlementAccess.l0BenefitChartDescription'),
        },
        {
          title: t('entitlementAccess.l0BenefitProfileTitle'),
          description: t('entitlementAccess.l0BenefitProfileDescription'),
        },
      ],
      primary: t('entitlementAccess.createAccount'),
    };
  }

  if (level === 'L1') {
    return {
      title: t('entitlementAccess.l1Title'),
      subtitle: t('entitlementAccess.l1Subtitle'),
      benefits: [
        {
          title: t('entitlementAccess.l1BenefitSessionsTitle'),
          description: t('entitlementAccess.l1BenefitSessionsDescription'),
        },
        {
          title: t('entitlementAccess.l1BenefitSummaryTitle'),
          description: t('entitlementAccess.l1BenefitSummaryDescription'),
        },
        {
          title: t('entitlementAccess.l1BenefitReviewTitle'),
          description: t('entitlementAccess.l1BenefitReviewDescription'),
        },
      ],
      primary: t('entitlementAccess.purchaseChallenge'),
    };
  }

  return {
    title: t('entitlementAccess.l23Title'),
    subtitle: t('entitlementAccess.l23Subtitle'),
    benefits: [
      {
        title: t('entitlementAccess.l23BenefitSessionsTitle'),
        description: t('entitlementAccess.l23BenefitSessionsDescription'),
      },
      {
        title: t('entitlementAccess.l23BenefitFeaturesTitle'),
        description: t('entitlementAccess.l23BenefitFeaturesDescription'),
      },
    ],
    primary: t('entitlementAccess.purchaseAgain'),
  };
}

export function EntitlementAccessDialog({ open, onOpenChange, level }: EntitlementAccessDialogProps) {
  const primaryActionRef = useRef<HTMLButtonElement>(null);
  const { user } = useAuth();
  const { isRTL, t } = useLanguage();
  const { openLoginDialog } = useLoginDialog();
  const normalizedLevel = normalizeUserLevel(level ?? user?.level);
  const copy = getTierCopy(normalizedLevel, t);
  const isGuest = normalizedLevel === 'L0';
  const isVipUpgrade = normalizedLevel === 'L2' || normalizedLevel === 'L3';

  const handlePrimaryAction = () => {
    const targetUrl = isGuest ? getSignUpUrl() : getPricingUrl(user?.pricing_url);
    if (targetUrl && typeof window !== 'undefined') {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSignIn = () => {
    onOpenChange(false);
    openLoginDialog();
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={onOpenChange}>
      <AlertDialogContent
        size="wide"
        data-entitlement-level={normalizedLevel}
        dir={isRTL ? 'rtl' : 'ltr'}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          primaryActionRef.current?.focus();
        }}
        overlayClassName="bg-black/75 backdrop-blur-[2px]"
        className="block rounded-[22px] border border-[#D98A3C] p-0 text-white shadow-[0_30px_90px_rgba(0,0,0,0.72)] sm:max-w-[520px]"
        style={{
          background: 'radial-gradient(circle at 0% 0%, rgba(119, 47, 2, 0.96) 0%, rgba(49, 18, 2, 0.98) 30%, rgba(13, 9, 6, 0.99) 76%)',
        }}>
        <div className="relative px-5 pb-5 pt-9 sm:px-8 sm:pt-8">
          <AlertDialogCancel
            size="icon"
            variant="ghost"
            aria-label={t('settings.close')}
            className="absolute right-2.5 top-2.5 size-8 rounded-full border-0 bg-transparent p-0 text-white shadow-none hover:bg-white/10 hover:text-white sm:right-3.5 sm:top-3.5">
            <X
              className="size-5"
              strokeWidth={2.2}
            />
          </AlertDialogCancel>

          <div className="flex justify-center">
            <Image
              src="/logo.svg"
              alt="AIFO"
              width={108}
              height={28}
              className="h-auto w-[84px] sm:w-[88px]"
            />
          </div>

          <div className="mt-5 text-center">
            <AlertDialogTitle className="text-[clamp(1.4rem,4vw,1.75rem)] font-semibold leading-[1.15] tracking-[-0.025em] text-white">{copy.title}</AlertDialogTitle>
            <AlertDialogDescription className="mt-2 text-sm leading-5 text-white/40 sm:text-[15px]">{copy.subtitle}</AlertDialogDescription>
          </div>

          <div className="mt-6 space-y-5 sm:mt-7 sm:space-y-7">
            {copy.benefits.map((benefit) => (
              <div
                key={benefit.title}
                className="flex items-center gap-3.5 sm:gap-4">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-[#2A1608]">
                  <Check
                    className="size-5 text-[#F98715]"
                    strokeWidth={2}
                  />
                </div>
                <div className="min-w-0 text-start">
                  <div className="text-base font-normal leading-5 text-white sm:text-[17px]">{benefit.title}</div>
                  <div className="mt-0.5 text-[13px] leading-5 text-white/38 sm:text-sm">{benefit.description}</div>
                </div>
              </div>
            ))}
          </div>

          {isVipUpgrade && (
            <div className="mt-5 sm:mt-6">
              <div className="text-sm text-white/40 sm:text-[15px]">{t('entitlementAccess.l23Progress', { current: 1, total: 2 })}</div>
              <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={2}
                aria-valuenow={1}
                className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/[0.08]">
                <div className="h-full w-1/2 rounded-full bg-[#F98715]" />
              </div>
            </div>
          )}

          <div className={cn('mt-6 space-y-2.5 sm:mt-7 sm:space-y-3', isVipUpgrade && 'sm:mt-7')}>
            <AlertDialogAction
              ref={primaryActionRef}
              variant="ghost"
              onClick={handlePrimaryAction}
              className="h-11 w-full rounded-xl !text-base font-normal text-white hover:text-white"
              style={{ backgroundColor: '#F98715' }}>
              {copy.primary}
            </AlertDialogAction>
            <AlertDialogCancel
              variant="outline"
              className="h-11 w-full rounded-xl border border-white/20 !text-base font-normal text-white shadow-none hover:text-white"
              style={{ backgroundColor: 'transparent' }}>
              {t('entitlementAccess.notNow')}
            </AlertDialogCancel>
          </div>

          <div className="mt-3 min-h-5 text-center text-[13px] leading-5 text-white/38">
            {isGuest ? (
              <>
                <span>{t('entitlementAccess.alreadyRegistered')} </span>
                <button
                  type="button"
                  onClick={handleSignIn}
                  className="text-[#F98715] hover:underline">
                  {t('entitlementAccess.signIn')}
                </button>
              </>
            ) : (
              t('entitlementAccess.benefitsEffective')
            )}
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
